import React, { useEffect, useMemo, useState } from 'react';

import { LineChart } from '@/components/shared/LineChart';
import { ModalSectionCard } from '@/components/shared/ModalSectionCard';
import { ModalSectionSecondaryText } from '@/components/shared/ModalSectionSecondaryText';
import { ModalSectionTitle } from '@/components/shared/ModalSectionTitle';
import {
	RadarChart,
	RadarChartData,
	RadarChartProps,
} from '@/components/shared/RadarChart';
import { TeamSongTags } from '@/components/TeamSongTags';
import { TeamSongThumbs } from '@/components/TeamSongThumbs';
import { BibleVerse, GetBiblePassageAnalysisMutation } from '@/graphql';
import { classNames } from '@/helpers/classNames';
import { useDarkMode } from '@/hooks/useDarkMode';
import { LimitedTeamSong, useTeamSongs } from '@/providers/TeamSongsProvider';
import { linearGradientDef } from '@nivo/core';

import AnalysisCard from '../AnalysisCard/AnalysisCard';
import ExampleChartLabel from '../ExampleChartLabel/ExampleChartLabel';
import styles from './BibleVerseEmotions.module.scss';

const BibleVerseEmotions: React.FC<{
	biblePassageAnalysis: GetBiblePassageAnalysisMutation['getBiblePassageAnalysis'];
	reference: string;
	songId: string;
	title?: string;
}> = ({ biblePassageAnalysis, reference, songId, title = 'Sermon song' }) => {
	const { isDarkModeOn } = useDarkMode();
	const { findTeamSongsById } = useTeamSongs();

	const teamSongs = findTeamSongsById([songId]);
	const teamSong = teamSongs[0];

	const data = biblePassageEmotionsToRadarChartData(
		biblePassageAnalysis,
		teamSong,
	);

	return (
		<AnalysisCard
			title={title}
			responsiveArea={
				<div className={styles['bible-verse-row']}>
					<div className={styles['reference']}>{reference}</div>
					<div className={styles['song']}>{teamSong?.name}</div>
				</div>
			}
		>
			<RadarChart
				data={data}
				keys={['verse', 'song']}
				indexBy="emotion"
				wrapperClassName={classNames(['mt-sm', styles['chart']])}
				colors={
					isDarkModeOn
						? ['#484c7a', '#56a0ff']
						: ['#b6b7d5', '#0040ff']
				}
			/>
		</AnalysisCard>
	);
};

export default BibleVerseEmotions;

export const biblePassageEmotionsToRadarChartData = (
	biblePassageAnalysis: GetBiblePassageAnalysisMutation['getBiblePassageAnalysis'],
	teamSong?: LimitedTeamSong,
) =>
	biblePassageAnalysis?.emotions.map((emotion) => {
		const songEmotionStrength =
			teamSong?.analysisOutput?.emotions?.find(
				(songEmotion) => songEmotion.emotion === emotion.emotion,
			)?.strength || 0;

		return {
			emotion: emotion.emotion,
			verse: emotion.strength || 0,
			song: songEmotionStrength,
		};
	}) as RadarChartData;
